import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    TextField,
    Typography
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CustomModal from 'src/components/modal/customModal';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import Logger from 'src/lib/Logger';

const logger = new Logger("shiftAMModal");

const STATUS_COLORS = {
    active: { bg: '#e6f4ea', color: '#1e7e34' },
    deActivate: { bg: '#fdecea', color: '#c62828' },
};

const getStatusChip = (status) => {
    const style = STATUS_COLORS[status] || {};
    return (
        <Chip
            label={status}
            size="small"
            sx={{
                ml: 1,
                height: 18,
                fontSize: 10,
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 600,
                textTransform: 'capitalize',
            }}
        />
    );
};

const ShiftAMModal = ({ open, close }) => {
    const [accountManagers, setAccountManagers] = useState([]);
    const [currentAm, setCurrentAm] = useState(null);
    const [newAm, setNewAm] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchAccountManagers = async () => {
        try {
            setLoading(true);
            const { result = [] } = await axiosGetCall('getAllAdmin');
            setAccountManagers(result);
        } catch (error) {
            logger.error("shiftAMModal.jsx ,fetchAccountManagers ,getAllAdmin", error);
            renderErrorMessage(error?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCurrentAm(null);
        setNewAm(null);
        close();
    };

    const handleSubmit = async () => {
        if (!currentAm || !newAm) {
            renderErrorMessage("Please select both account managers.");
            return;
        }

        if (currentAm.email === newAm.email) {
            renderErrorMessage("Current A.M. and new A.M. cannot be same.");
            return;
        }

        try {
            setSubmitting(true);
            const { message, updatedCount } = await axiosPostCall('shiftAM', {
                currentAm: currentAm.email,
                newAm: newAm.email,
            });
            renderSuccessMessage(`${message} ${updatedCount || 0} user(s) updated.`);
            handleClose();
        } catch (error) {
            logger.error("shiftAMModal.jsx ,handleSubmit ,shiftAM", error);
            renderErrorMessage(error?.message);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (open) fetchAccountManagers();
    }, [open]);


    const renderOption = (props, option) => (
        <Box component="li" {...props} key={option.email} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{option.email}</Typography>
            {getStatusChip(option.status)}
        </Box>
    );

    const loadingAdornment = (params) => ({
        ...params.InputProps,
        endAdornment: (
            <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
            </>
        ),
    });

    const description = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
                Move all users from one account manager to another.
            </Typography>

            <Autocomplete
                size="small"
                options={accountManagers}
                value={currentAm}
                loading={loading}
                getOptionLabel={(option) => option.email || ''}
                isOptionEqualToValue={(option, value) => option.email === value.email}
                onChange={(_, value) => {
                    setCurrentAm(value);
                    if (newAm && value?.email === newAm.email) setNewAm(null);
                }}
                renderOption={renderOption}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Current Account Manager"
                        InputProps={loadingAdornment(params)}
                    />
                )}
            />

            <Autocomplete
                size="small"
                options={accountManagers.filter(({ email, status, can_accept_am, role, deleted }) =>
                    email !== currentAm?.email &&
                    status === 'active' &&
                    can_accept_am === true &&
                    (role === 'admin' || role === 'agent') &&
                    deleted === false
                )}
                value={newAm}
                loading={loading}
                getOptionLabel={(option) => option.email || ''}
                isOptionEqualToValue={(option, value) => option.email === value.email}
                onChange={(_, value) => setNewAm(value)}
                renderOption={renderOption}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="New Account Manager"
                        InputProps={loadingAdornment(params)}
                    />
                )}
            />
        </Box>
    );

    const action = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
                Cancel
            </Button>
            <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress color="inherit" size={16} /> : <SwapHorizIcon />}
                disabled={submitting || loading}
                onClick={handleSubmit}
                sx={{
                    backgroundColor: 'rgb(33, 43, 54)',
                    '&:hover': { backgroundColor: 'rgb(69, 79, 91)' }
                }}
            >
                Shift AM
            </Button>
        </Box>
    );

    return (
        <CustomModal
            open={open}
            close={handleClose}
            title="Shift AM"
            description={description}
            action={action}
            width={460}
        />
    );
};

export default ShiftAMModal;
